import { z } from 'zod';
import { documentsInstanceSourceReferenceSchema, documentsInstanceStatusSchema, documentsSha256Schema, } from './documents-instance.contract.js';
export const documentsInsuranceClaimsScopeCompletionCapability = 'documents.insurance-claim-scope-completion.v1';
export const documentsInsuranceClaimsScopeCompletionSlot = 'insurance-claims.scope-completion';
export const documentsInsuranceClaimsScopeCompletionProviderId = 'documents.server.insurance-claims-scope-completion';
export const documentsInsuranceClaimsScopeSourceCapability = 'insurance-claims.scopes.v1';
export const documentsInsuranceClaimsScopeCompletionResourceType = 'insurance-claims.scope-completion-certificate';
const idSchema = z.string().uuid();
const sourceIdentitySchema = z.strictObject({
    claimId: idSchema,
    projectId: idSchema,
    scopeId: idSchema,
});
export function documentsInsuranceClaimsScopeCompletionSourceResourceId(input) {
    const value = sourceIdentitySchema.parse({
        claimId: input.claimId,
        projectId: input.projectId,
        scopeId: input.scopeId,
    });
    return `${value.claimId}:${value.projectId}:${value.scopeId}`;
}
export function documentsInsuranceClaimsScopeCompletionSourceReference(input) {
    return documentsInstanceSourceReferenceSchema.parse({
        capability: documentsInsuranceClaimsScopeSourceCapability,
        resourceType: documentsInsuranceClaimsScopeCompletionResourceType,
        resourceId: documentsInsuranceClaimsScopeCompletionSourceResourceId(input),
        displayLabel: input.displayLabel ?? null,
    });
}
export const documentsInsuranceClaimsScopeCompletionRequestSchema = z.strictObject({
    consumerModuleId: z.literal('insurance-claims'),
    requestedCapability: z.literal(documentsInsuranceClaimsScopeCompletionCapability),
    resourceType: z.literal('insurance-claims.scope'),
    resourceId: idSchema,
    claimId: idSchema,
    projectId: idSchema,
});
const signatureFactSchema = z.discriminatedUnion('state', [
    z.strictObject({ state: z.literal('missing') }),
    z.strictObject({
        state: z.literal('present'),
        signatureId: idSchema,
        signatureSha256: documentsSha256Schema,
        signedAt: z.string().datetime({ offset: true }),
    }),
]);
const finalArtifactFactSchema = z.discriminatedUnion('state', [
    z.strictObject({ state: z.literal('missing') }),
    z.strictObject({
        state: z.literal('present'),
        objectId: idSchema,
        contentType: z.string().trim().min(1).max(255),
        sizeBytes: z.number().int().positive().safe(),
        sha256: documentsSha256Schema,
        verifiedAt: z.string().datetime({ offset: true }),
        attachedAt: z.string().datetime({ offset: true }),
    }),
]);
export const documentsInsuranceClaimsScopeCompletionFactSchema = z.strictObject({
    capability: z.literal(documentsInsuranceClaimsScopeCompletionCapability),
    claimId: idSchema,
    projectId: idSchema,
    scopeId: idSchema,
    instanceId: idSchema,
    sourceVersion: z.number().int().positive(),
    contentHash: documentsSha256Schema,
    status: documentsInstanceStatusSchema,
    signature: signatureFactSchema,
    artifact: finalArtifactFactSchema,
    observedAt: z.string().datetime({ offset: true }),
});
export const documentsInsuranceClaimsScopeCompletionResponseSchema = z.strictObject({
    state: z.literal('present'),
    capability: z.literal(documentsInsuranceClaimsScopeCompletionCapability),
    claimId: idSchema,
    projectId: idSchema,
    scopeId: idSchema,
    facts: z.array(documentsInsuranceClaimsScopeCompletionFactSchema).max(500),
}).superRefine((value, context) => {
    const instanceIds = value.facts.map(({ instanceId }) => instanceId);
    if (new Set(instanceIds).size !== instanceIds.length) {
        context.addIssue({
            code: 'custom', path: ['facts'],
            message: 'Scope completion facts must contain each document instance once.',
        });
    }
    value.facts.forEach((fact, index) => {
        if (fact.claimId !== value.claimId || fact.projectId !== value.projectId ||
            fact.scopeId !== value.scopeId) {
            context.addIssue({
                code: 'custom', path: ['facts', index],
                message: 'Scope completion facts must match the requested Claim, Project, and scope.',
            });
        }
    });
});
//# sourceMappingURL=documents-insurance-claims-scope-completion.contract.js.map